import { library } from '@fortawesome/fontawesome-svg-core';
import { faBell, faCheckCircle, faExclamationTriangle, faInfoCircle, faTimes, faTimesCircle } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { TFunction, useTranslator } from '@Localization';
import { ClickAwayListener } from '@mui/base';
import { Button, Dropdown, IconButton, Menu, MenuButton, MenuItem } from '@mui/joy';
import parse from 'html-react-parser';
import { observer } from 'mobx-react-lite';
import { ReactElement, ReactNode, useState } from 'react';
import { Trans } from 'react-i18next';
import TimeAgo from 'react-timeago';
import sanitize from 'sanitize-html';
import { S_Link } from '../../../../design/links/S_Link';
import { DownloadExportModal } from '../../../../modules/projects/details/exportData/ui/downloadExportModal/DownloadExportModal';
import { Home } from '../../../routes/config/Home';
import { DownloadExportContainer } from '../containers/DownloadExport.container';
import { INotificationModel } from '../containers/NotificationsHub.container';
import { ILocalizable, NotificationLevel, NotificationType } from '../models/notification.model';

library.add(faBell, faTimes, faCheckCircle, faTimesCircle, faExclamationTriangle, faInfoCircle);

interface INotificationsHubProps {
  notifications: INotificationModel[];
  onClearAll: () => Promise<void>;
  onToggle: () => Promise<void>;
}

export const NotificationsHub = observer((props: INotificationsHubProps): ReactElement => {
  const [isOpen, setIsOpen] = useState<boolean>(false);

  const notReadNotifications = props.notifications.filter((n) => !n.wasRead);
  const { t } = useTranslator();

  const getNotificationIcon = (level: NotificationLevel): ReactElement => {
    switch (level) {
      case NotificationLevel.SUCCESS:
        return (
          <FontAwesomeIcon
            icon={faCheckCircle}
            className='success-icon'
          />
        );
      case NotificationLevel.ERROR:
        return (
          <FontAwesomeIcon
            icon={faTimesCircle}
            className='error-icon'
          />
        );
      case NotificationLevel.WARNING:
        return (
          <FontAwesomeIcon
            icon={faExclamationTriangle}
            className='warning-icon'
          />
        );
      case NotificationLevel.INFO:
      default:
        return (
          <FontAwesomeIcon
            icon={faInfoCircle}
            className='info-icon'
          />
        );
    }
  };

  const getLocalizedContent = (t: TFunction, content?: string | ILocalizable | ReactNode): ReactNode => {
    if (content === undefined || content === null) {
      return content;
    }

    if (typeof content === 'string') {
      return parse(sanitize(content));
    }

    const isLocalizable = (content: string | ILocalizable | ReactNode): content is ILocalizable => {
      const localizable = content as ILocalizable;
      return !!localizable.template;
    };

    if (isLocalizable(content)) {
      return (
        <Trans
          i18nKey={content.template}
          values={content.data || ({} as Record<string, string>)}
          components={{ b: <b /> }}
        />
      );
    }

    return content;
  };

  const RenderNotification = ({ notification }: { notification: INotificationModel }): ReactElement => {
    const { t } = useTranslator();
    return (
      <MenuItem
        key={notification.id}
        className='notification-hub-single-item'
        onClick={(e) => {
          e.stopPropagation();
        }}
      >
        <div className='top'>
          <div className='name elipsis'>
            <>{getNotificationIcon(notification.level)}</>
            <>{getLocalizedContent(t, notification.title)}</>
          </div>
          <div className='ago'>
            <TimeAgo date={notification.date} />
            <IconButton
              size='sm'
              onClick={(): void => {
                notification.onRemove(notification.id);
              }}
            >
              <FontAwesomeIcon icon={faTimes} />
            </IconButton>
          </div>
        </div>
        <div className='body'>
          <div>{getLocalizedContent(t, notification.body)}</div>
          {notification.projectExportId && renderExportDownloadLink(notification)}
          {notification.workspaceId && notification.jobId && notification.jobType && renderJobLink(notification)}
          {notification.workspaceId && !notification.jobId && !notification.jobType && renderCloudCostUsageLink(notification)}
        </div>
      </MenuItem>
    );
  };

  const renderExportDownloadLink = (notification: INotificationModel): ReactNode => (
    <div>
      {notification.exportDataFilter && notification.projectExportId && (
        <DownloadExportContainer
          projectExportId={notification.projectExportId}
          exportDataFilter={notification.exportDataFilter}
        />
      )}
    </div>
  );

  const renderCloudCostUsageLink = (notification: INotificationModel): ReactNode => (
    <div>
      <S_Link
        to={Home.User.CloudCosts.History.withParams({ workspaceId: notification.workspaceId ?? '' })}
        className='notification-link'
        color='anchor'
      >
        {t('click_here_to_see_cloud_usage')}
      </S_Link>
    </div>
  );

  const renderJobLink = (notification: INotificationModel): ReactNode => {
    const getLink = (): string => {
      let link = '';

      switch (notification.notificationType) {
        case NotificationType.JobFailed:
        case NotificationType.JobRetrying:
          link =
            notification.jobType?.toLowerCase() === 'training'
              ? Home.Trainings.Details.Settings.withParams({ workspaceId: notification.workspaceId ?? '', jobId: notification.jobId ?? '' })
              : Home.Inferences.Details.Datasets.withParams({ workspaceId: notification.workspaceId ?? '', jobId: notification.jobId ?? '' });
          break;
        case NotificationType.JobFinished:
        case NotificationType.JobLongRunTime:
          link =
            notification.jobType?.toLowerCase() === 'training'
              ? Home.Trainings.Details.Metrics.withParams({ workspaceId: notification.workspaceId ?? '', jobId: notification.jobId ?? '' })
              : Home.Inferences.Details.Metrics.withParams({ workspaceId: notification.workspaceId ?? '', jobId: notification.jobId ?? '' });
          break;
        case undefined:
          break;
      }

      return link;
    };

    return (
      <div>
        <S_Link
          to={getLink()}
          className='notification-link'
          color='anchor'
        >
          {t('click_here_to_see_job_details')}
        </S_Link>
      </div>
    );
  };

  return (
    <>
      <ClickAwayListener
        onClickAway={() => {
          setIsOpen(false);
        }}
      >
        <div>
          <Dropdown open={isOpen}>
            <MenuButton
              onClick={() => {
                setIsOpen((x) => !x);
                void props.onToggle();
              }}
              className='notification-hub'
              slots={{ root: IconButton }}
              slotProps={{ root: { variant: 'topbar' } }}
            >
              <FontAwesomeIcon
                fixedWidth
                icon={faBell}
                className={`${notReadNotifications.length > 0 ? 'active' : 'not-active'} notification-icon`}
              />
              {notReadNotifications.length > 0 && <span className='notify-badge'>{notReadNotifications.length > 9 ? '9+' : notReadNotifications.length}</span>}
            </MenuButton>
            <Menu
              className='no-hover notification-hub-dropdown'
              placement='bottom-end'
            >
              <MenuItem className='dropdown-header'>
                <span>
                  {t('notifications')} ({props.notifications.length})
                </span>
                {props.notifications.length > 0 && (
                  <Button
                    onClick={() => {
                      void props.onClearAll();
                    }}
                    size='sm'
                    color='neutral'
                    variant='plain'
                  >
                    {t('clear_all')}
                  </Button>
                )}
              </MenuItem>
              {props.notifications.map((notification) => (
                <RenderNotification
                  key={notification.id}
                  notification={notification}
                />
              ))}
            </Menu>
          </Dropdown>
        </div>
      </ClickAwayListener>
      <DownloadExportModal />
    </>
  );
});
